export type Hsb = {
  hue: number;
  saturation: number;
  brightness: number;
};

export type CartoonShape = "star" | "cat" | "bot" | "leaf" | "cloud";

export type RoundColor = Hsb & {
  hex: string;
  character: string;
  part: string;
  shape: CartoonShape;
};

export const ROUND_COUNT = 10;
export const MAX_TOTAL = ROUND_COUNT * 100;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const normalizeHue = (hue: number) => ((hue % 360) + 360) % 360;

export const hsbToRgb = ({ hue, saturation, brightness }: Hsb) => {
  const h = normalizeHue(hue);
  const s = saturation / 100;
  const v = brightness / 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  const [r1, g1, b1] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];

  return {
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  };
};

export const hsbToHex = (color: Hsb) => {
  const { r, g, b } = hsbToRgb(color);
  return `#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`;
};

const pivotRgb = (value: number) => {
  const normalized = value / 255;
  return normalized > 0.04045 ? ((normalized + 0.055) / 1.055) ** 2.4 : normalized / 12.92;
};

const pivotXyz = (value: number) =>
  value > 0.008856 ? Math.cbrt(value) : 7.787 * value + 16 / 116;

const hsbToLab = (color: Hsb) => {
  const { r, g, b } = hsbToRgb(color);
  const linearR = pivotRgb(r);
  const linearG = pivotRgb(g);
  const linearB = pivotRgb(b);
  const x = (linearR * 0.4124 + linearG * 0.3576 + linearB * 0.1805) / 0.95047;
  const y = linearR * 0.2126 + linearG * 0.7152 + linearB * 0.0722;
  const z = (linearR * 0.0193 + linearG * 0.1192 + linearB * 0.9505) / 1.08883;
  const fx = pivotXyz(x);
  const fy = pivotXyz(y);
  const fz = pivotXyz(z);
  return {
    l: 116 * fy - 16,
    a: 500 * (fx - fy),
    b: 200 * (fy - fz),
  };
};

export const colorDistance = (left: Hsb, right: Hsb) => {
  const a = hsbToLab(left);
  const b = hsbToLab(right);
  return Math.sqrt((a.l - b.l) ** 2 + (a.a - b.a) ** 2 + (a.b - b.b) ** 2);
};

export const roundScore = (target: Hsb, guess: Hsb) => {
  const distance = colorDistance(target, guess);
  return Math.round(clamp(100 - 2 * distance, 0, 100));
};

export const scoreLabel = (score: number) => {
  if (score >= 92) return "거의 완벽해요";
  if (score >= 78) return "정확한 감각이에요";
  if (score >= 60) return "잘 맞췄어요";
  if (score >= 42) return "방향은 맞았어요";
  return "다음 라운드에서 만회해요";
};

export const feedbackLabel = (distance: number) => {
  if (distance < 2) return "🎯 완벽해요";
  if (distance < 6) return "😊 거의 같아요";
  if (distance < 15) return "🤔 비슷해요";
  return "😅 다시 도전!";
};

export const HUE_GRADIENT =
  "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)";

const sampleStops = (sample: (t: number) => string, count = 8) =>
  Array.from({ length: count + 1 }, (_, index) => sample(index / count)).join(", ");

export const saturationGradient = ({ hue, brightness }: Hsb) =>
  `linear-gradient(to right, ${sampleStops((t) =>
    hsbToHex({ hue, saturation: t * 100, brightness }),
  )})`;

export const brightnessGradient = ({ hue, saturation }: Hsb) =>
  `linear-gradient(to right, ${sampleStops((t) =>
    hsbToHex({ hue, saturation, brightness: t * 100 }),
  )})`;

export const totalLabel = (total: number) => {
  if (total >= 900) return "색 감각 마스터";
  if (total >= 750) return "색에 진심인 플레이어";
  if (total >= 600) return "안정된 색 감각";
  if (total >= 400) return "조금 더 다듬어 보세요";
  return "꾸준히 연습해 보세요";
};

const mulberry32 = (seed: number) => {
  let value = seed;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
};

export const randomSeed = () =>
  Math.floor((Math.random() * 0xffffffff) >>> 0) || 20260514;

const prompts: ReadonlyArray<{ character: string; part: string; shape: CartoonShape }> = [
  { character: "별빛 친구", part: "귀 끝", shape: "star" },
  { character: "고양이 가면", part: "리본", shape: "cat" },
  { character: "둥근 로봇", part: "안테나", shape: "bot" },
  { character: "잎사귀 요정", part: "망토", shape: "leaf" },
  { character: "구름 여행자", part: "스카프", shape: "cloud" },
  { character: "달빛 토끼", part: "귀 안쪽", shape: "star" },
  { character: "물방울 정령", part: "보석", shape: "cat" },
  { character: "햇살 인형", part: "단추", shape: "bot" },
  { character: "숲속 사슴", part: "뿔 끝", shape: "leaf" },
  { character: "바다 친구", part: "지느러미", shape: "cloud" },
];

export const createPalette = (seed = randomSeed()): RoundColor[] => {
  const random = mulberry32(seed);
  const baseHue = Math.floor(random() * 360);
  return Array.from({ length: ROUND_COUNT }, (_, index) => {
    const hue = Math.round((baseHue + index * 47 + random() * 90) % 360);
    const saturation = Math.round(clamp(45 + random() * 50, 35, 95));
    const brightness = Math.round(clamp(48 + random() * 46, 40, 94));
    const color = { hue, saturation, brightness };
    const prompt = prompts[index % prompts.length];
    return { ...color, ...prompt, hex: hsbToHex(color) };
  });
};
